import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import api from '../../services/api';
import { Card, CardBody, CardTitle, CardText, Button, Row, Col, Badge, Alert, Nav, NavItem, NavLink, TabContent, TabPane, Progress } from 'reactstrap';
import { Link } from 'react-router-dom';
import classnames from 'classnames';


const GigDetailsPage = ({ currentUser }) => {
  const handleMessageLab = async () => {
    if (!gig || !gig.user_id) return;
    const payload = {
      subject: `Inquiry about Gig: ${gig.title}`,
      participantIds: [gig.user_id],
      body: 'Hi, I am interested in this gig.',
      context: { gigId: gig.id }
    };
    try {
      // Get token from currentUser or localStorage
      const token = currentUser?.token || localStorage.getItem('token');
      const res = await fetch('/api/inbox/conversations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': 'Bearer ' + token } : {})
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setMessageSent(true);
      } else {
        alert('Failed to start conversation.');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };
  const { id } = useParams();
  const history = useHistory();
  const [gig, setGig] = useState(null);
  const role = currentUser?.role;
  const [applied, setApplied] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [showCredentialUpload, setShowCredentialUpload] = useState(false);
  const [credentialUploaded, setCredentialUploaded] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [applicantsError, setApplicantsError] = useState(null);

  const isLab = role === 'Lab';
  const isWorker = role === 'Worker';

  const handleApplicationStatusUpdate = (applicationId, status) => {
    setApplicants(prevApplicants =>
      prevApplicants.map(app =>
        app.id === applicationId ? { ...app, status } : app
      )
    );
  };

  const handleAccept = (applicationId) => {
    api.acceptApplication(applicationId)
      .then(response => {
        handleApplicationStatusUpdate(applicationId, response.data.status);
      })
      .catch(error => {
        console.error("Error accepting application:", error);
        // Optionally, show an error message to the user
      });
  };

  const handleReject = (applicationId) => {
    api.rejectApplication(applicationId)
      .then(response => {
        handleApplicationStatusUpdate(applicationId, response.data.status);
      })
      .catch(error => {
        console.error("Error rejecting application:", error);
        // Optionally, show an error message to the user
      });
  };

  useEffect(() => {
    setLoading(true);
    api.getGigById(id)
      .then(response => {
        setGig(response.data);
        setLoading(false);
        
        // Check if current user has already applied to this gig
        if (isWorker) {
          api.checkApplicationStatus(id)
            .then(statusResponse => {
              if (statusResponse.data.hasApplied) {
                setApplied(true);
              }
            })
            .catch(error => {
              console.error("Error checking application status:", error);
            });
        }
      })
      .catch(error => {
        console.error("Error fetching gig details:", error);
        setError(error);
        setLoading(false);
      });
  }, [id, isWorker]);

  useEffect(() => {
    if (activeTab === '2' && isLab && gig) {
      setLoadingApplicants(true);
      api.getGigApplications(gig.id)
        .then(response => {
          setApplicants(response.data);
          setLoadingApplicants(false);
        })
        .catch(error => {
          setApplicantsError(error);
          setLoadingApplicants(false);
        });
    }
  }, [activeTab, isLab, gig]);

  // Helper: Render Lab Info
  const [labInfoEdit, setLabInfoEdit] = useState('');
  const [faqEdit, setFaqEdit] = useState('');
  const [labInfoEditMode, setLabInfoEditMode] = useState(false);
  const [faqEditMode, setFaqEditMode] = useState(false);
  const [labInfoError, setLabInfoError] = useState(null);
  const [faqError, setFaqError] = useState(null);

  const handleSaveLabInfo = async () => {
    try {
      await api.updateGig(gig.id, { labInfo: labInfoEdit });
      setGig({ ...gig, lab_info: labInfoEdit });
      setLabInfoEditMode(false);
      setLabInfoError(null);
    } catch (err) {
      setLabInfoError(err.response?.data?.error || 'Failed to update Lab Info');
    }
  };

  const handleSaveFaq = async () => {
    try {
      await api.updateGig(gig.id, { faq: faqEdit });
      setGig({ ...gig, faq: faqEdit });
      setFaqEditMode(false);
      setFaqError(null);
    } catch (err) {
      setFaqError(err.response?.data?.error || 'Failed to update FAQ');
    }
  };

  const LabInfoSection = () => (
    <Card className="mb-3">
      <CardBody>
        <CardTitle tag="h5">Lab Info</CardTitle>
        {isLab && labInfoEditMode ? (
          <>
            <textarea value={labInfoEdit} onChange={e => setLabInfoEdit(e.target.value)} rows={6} className="form-control mb-2" />
            <Button color="primary" onClick={handleSaveLabInfo}>Save</Button>{' '}
            <Button color="secondary" onClick={() => setLabInfoEditMode(false)}>Cancel</Button>
            {labInfoError && <Alert color="danger" className="mt-2">{labInfoError}</Alert>}
          </>
        ) : (
          <>
            <CardText>{gig?.lab_info || 'Lab info not available.'}</CardText>
            {isLab && <Button color="link" onClick={() => { setLabInfoEditMode(true); setLabInfoEdit(gig?.lab_info || ''); }}>Edit</Button>}
          </>
        )}
      </CardBody>
    </Card>
  );

  const FAQSection = () => (
    <Card className="mb-3">
      <CardBody>
        <CardTitle tag="h5">FAQ & Expectations</CardTitle>
        {isLab && faqEditMode ? (
          <>
            <textarea value={faqEdit} onChange={e => setFaqEdit(e.target.value)} rows={8} className="form-control mb-2" />
            <Button color="primary" onClick={handleSaveFaq}>Save</Button>{' '}
            <Button color="secondary" onClick={() => setFaqEditMode(false)}>Cancel</Button>
            {faqError && <Alert color="danger" className="mt-2">{faqError}</Alert>}
          </>
        ) : (
          <>
            <CardText>{gig?.faq || 'No FAQ available.'}</CardText>
            {isLab && <Button color="link" onClick={() => { setFaqEditMode(true); setFaqEdit(gig?.faq || ''); }}>Edit</Button>}
          </>
        )}
      </CardBody>
    </Card>
  );

  if (loading) {
    return <div className="container mt-4"><h4>Loading gig...</h4></div>;
  }

  if (error) {
    return <div className="container mt-4"><Alert color="danger">Error loading gig details.</Alert></div>;
  }

  if (!gig) {
    return <div className="container mt-4"><h4>Gig not found.</h4></div>;
  }

  return (
    <div className="container mt-4">
      <Button color="link" onClick={() => history.goBack()}>&larr; Back to Gigs</Button>
      <Card className="mb-4">
        <CardBody>
          <Row>
            <Col md="8">
              {/* Lab Info Banner */}
              {gig.lab_profile && (
                <div className="bg-light p-2 rounded-top d-flex align-items-center mb-3">
                  <img
                    src={gig.lab_profile.logo_url || '/logo-LabLeap446.png'}
                    alt="Lab Logo"
                    className="me-2 logo-small"
                    style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', background: '#eee' }}
                  />
                  <span className="fw-bold text-primary"><i className="fa fa-flask me-1" />Lab:</span>
                  <span className="ms-2">{gig.lab_profile.lab_name || 'Unknown Lab'}</span>
                </div>
              )}
              <CardTitle tag="h3">{gig?.title} <Badge color="success">{gig?.status || 'Open'}</Badge> {gig?.urgent && <Badge color="danger">Urgent</Badge>} {gig?.remote && <Badge color="info">Remote</Badge>}</CardTitle>
              <CardText><strong>Compensation:</strong> {gig?.pay_rate} <span className="ms-3"><strong>Duration:</strong> {gig?.duration}</span></CardText>
              <Progress value={gig?.status === 'Open' ? 33 : gig?.status === 'In Progress' ? 66 : 100} className="mb-2" />
              <Nav tabs>
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === '1' })} onClick={() => setActiveTab('1')}>Details</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === '2' })} onClick={() => setActiveTab('2')}>Applicants</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === '3' })} onClick={() => setActiveTab('3')}>Lab Info</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === '4' })} onClick={() => setActiveTab('4')}>FAQ</NavLink>
                </NavItem>
              </Nav>
              <TabContent activeTab={activeTab} className="mt-3">
                <TabPane tabId="1">
                  <CardText><strong>Description:</strong> {gig?.description}</CardText>
                  <CardText><strong>Required Skills:</strong> {Array.isArray(gig?.required_skills) ? gig.required_skills.join(', ') : gig?.required_skills}</CardText>
                  <CardText><strong>Required Certifications:</strong> {Array.isArray(gig?.required_certifications) ? gig.required_certifications.join(', ') : gig?.required_certifications}</CardText>
                  <CardText><strong>Location:</strong> {gig?.location}</CardText>
                  {isWorker && !applied && <Button color="primary" onClick={() => {
                    api.applyToGig(gig?.id).then(() => setApplied(true));
                  }}>Apply</Button>}
                  {isWorker && applied && <Button color="success" disabled>Application Submitted</Button>}
                  {isWorker && <Button color="secondary" className="ms-2" onClick={handleMessageLab}>Message Lab</Button>}
                  {isWorker && <Button color="info" className="ms-2" onClick={() => setShowCredentialUpload(true)}>Upload Credential</Button>}
                  {messageSent && <Alert color="info" className="mt-2">Demo: Message sent to lab!</Alert>}
                  {showCredentialUpload && !credentialUploaded && (
                    <div className="mt-3">
                      <input type="file" onChange={() => setCredentialUploaded(true)} />
                      <Button color="primary" className="ms-2" onClick={() => setCredentialUploaded(true)}>Submit Credential</Button>
                    </div>
                  )}
                  {credentialUploaded && <Alert color="success" className="mt-2">Demo: Credential uploaded!</Alert>}
                </TabPane>
                <TabPane tabId="2">
                  {isLab ? (
                    loadingApplicants ? <p>Loading applicants...</p> :
                    applicantsError ? <Alert color="danger">Error loading applicants.</Alert> :
                    <Row>
                      {applicants.map(app => (
                          <Col md={6} key={app.id} className="mb-3">
                            <Card>
                              <CardBody>
                                <Row>
                                  <Col xs="3">
                                    <img src={app.avatar} alt={app.name} className="img-fluid rounded-circle" style={{ width: 48, height: 48 }} />
                                  </Col>
                                  <Col xs="9">
                                    <CardTitle tag="h5"><Link to={`/app/profile/${app.user_id}`}>{app.name}</Link> <Badge color={app.status === 'accepted' ? 'success' : 'info'}>{app.status}</Badge></CardTitle>
                                    <CardText>
                                      <b>Skills:</b> {Array.isArray(app.skills) ? app.skills.join(', ') : app.skills}<br/>
                                      <b>Certifications:</b> {Array.isArray(app.certifications) ? app.certifications.join(', ') : app.certifications}<br/>
                                      <b>Bio:</b> {app.bio}<br/>
                                      <b>Rating:</b> {app.rating} / 5
                                    </CardText>
                                    {app.status === 'pending' && (
                                      <>
                                        <Button color="success" onClick={() => handleAccept(app.id)}>Accept</Button>{' '}
                                        <Button color="danger" onClick={() => handleReject(app.id)}>Reject</Button>
                                      </>
                                    )}
                                    {app.status === 'accepted' && (
                                      <Alert color="success">
                                        Application accepted! <br />
                                        Contact: {app.email}
                                      </Alert>
                                    )}
                                    {app.status === 'rejected' && (
                                      <Alert color="danger">Application Rejected</Alert>
                                    )}
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>
                        ))}
                    </Row>
                  ) : (
                    <Alert color="info">Only labs can view applicants.</Alert>
                  )}
                </TabPane>
                <TabPane tabId="3">
                  <LabInfoSection />
                </TabPane>
                <TabPane tabId="4">
                  <FAQSection />
                </TabPane>
              </TabContent>
            </Col>
            <Col md="4">
              <LabInfoSection />
            </Col>
          </Row>
        </CardBody>
      </Card>
    </div>
  );
};

// Connect to Redux to get currentUser
export default compose(
  connect(state => ({ currentUser: state.auth.currentUser }))
)(GigDetailsPage);
